export enum InsuranceCategory {
    Life = 0,
    Health = 1,
    Accident = 2,
    Other = 3,
}

export enum InsurancePlanCode {
    VidaTemporalNivelado = 'VidaTemporalNivelado',
    VidaTerminoAnualRenovable = 'VidaTerminoAnualRenovable',
    VidaDevolucionDePrimas = 'VidaDevolucionDePrimas',
    SeguroMedicoCatastrofico = 'SeguroMedicoCatastrofico',
    EnfermedadesGraves = 'EnfermedadesGraves',
    Megalife = 'Megalife',
    AccidentesPersonalesIndividual = 'AccidentesPersonalesIndividual',
    AccidentesPersonalesColectivo = 'AccidentesPersonalesColectivo',
    RespaldoColonial = 'RespaldoColonial',
    UltimosGastos = 'UltimosGastos'
}

export interface InsuranceTypeDto {
    id: number;
    name: string;
    lob: number;
    slob: number;
    /** Core plan code (SlobLifePlan.PlanCode) used at issuance; null = resolved from SLOB. */
    planCode?: number | null;
    category: InsuranceCategory;
    bankId?: number | null;
    durationMin?: number | null;
    durationMax?: number | null;
    minAge: number;
    maxAge: number;
    maxDurationAge: number;
    childMinAge?: number;
    childMaxAge?: number;
    childMaxDurationAge?: number;
    familyAllowed: boolean;
    /** Master switch: hidden products are not offered in the client app. */
    visible?: boolean;
    /** For Colonial products, the caller's bank choice (globals/regionals); null otherwise. */
    visibleForBank?: boolean | null;
    allowBeneficiaries: boolean;
    allowDependents: boolean;
    requiresOccupation: boolean;
    priority: number;
    formulaId: string;
    icon: string;
    description: string;
    benefits: string[];
    currencies: InsuranceTypeCurrencyDto[];
    coverages: InsuranceTypeCoverageDto[];
    questionGroups: InsuranceQuestionGroupDto[];
    requirementBands: InsuranceRequirementBandDto[];
    seriousIllnessPlans?: EnfermedadesGravesPlanDto[];
    catastrophicMedicalAgePricings?: CatastrophicMedicalAgePricingDto[];
    coveragePlans?: CoveragePlanDto[];
    ageAmountLimits?: AgeAmountLimitDto[];
}

/** Max sum assured of the primary coverage for primary insureds aged ageFrom..ageTo, per currency. */
export interface AgeAmountLimitDto {
    ageFrom: number;
    ageTo: number;
    currency: string;
    maxAmount: number;
}

/** Whole-policy minimum net premium (before tax) for one of the product's currencies. */
export interface CurrencyMinPremiumDto {
    currencyId: number;
    minPremium: number | null;
}

export interface CoveragePlanDto {
    id: number;
    name: string;
    items: CoveragePlanItemDto[];
}

export interface CoveragePlanItemDto {
    id: number;
    coverageName: string;
    coverageId: number;
    sumInsured: number;
    premium: number;
}

export interface EnfermedadesGravesPlanDto {
    id: number;
    name: string;
    diagnosisPayout: number;
    survivalPayout: number;
    survivalPeriodMonths: number;
    premiums: EnfermedadesGravesPremiumDto[];
    coverages: EnfermedadesGravesPlanCoverageDto[];
}

export interface EnfermedadesGravesPremiumDto {
    id: number;
    minAge: number;
    maxAge: number;
    amount: number;
}

export interface EnfermedadesGravesPlanCoverageDto {
    id: number;
    coverageId: number;
    coverageName: string;
    description?: string | null;
}

export interface CatastrophicMedicalAgePricingDto {
    id: number;
    personInsuranceCurrencyId: number;
    minAge: number;
    maxAge: number;
    gender: string;
    isForChild: boolean;
    premiumAmount: number;
}

export interface InsuranceTypeCurrencyDto {
    id: number;
    currency: string;
    min: number;
    max: number;
    minAmount: number;
    maxAmount: number;
    isFixedLimit: boolean;
    minPremium?: number | null;
}

export interface InsuranceTypeCoverageDto {
    id: number;
    name: string;
    description: string;
    isRequired: boolean;
    insurePrimaryFraction: number;
    maxAge: number;
    maxDurationAge: number;
    isCalculated: boolean;
    tariffType?: string;
    currencies: InsuranceTypeCurrencyDto[];
    categories: InsuranceTypeCoverageCategoryDto[];
}

export interface InsuranceTypeCoverageCategoryDto {
    category: string;
    rates: InsuranceTypeCoverageRateDto[];
}

export interface InsuranceTypeCoverageRateDto {
    ageFrom: number;
    ageTo: number;
    rate: number;
}

export interface InsuranceQuestionGroupDto {
    id: number;
    title: string;
    description: string;
    questions: InsuranceQuestionDto[];
    phase?: string;
}

export interface InsuranceQuestionDto {
    id: number;
    title: string;
    type: string;
    required: boolean;
}

export interface CreateQuestionGroupLinkDto {
    questionGroupId: number;
    phase: string;
}

export interface CreateInsuranceTypeDto {
    lob: number;
    slob: number;
    /** Core plan code (SlobLifePlan.PlanCode) used at issuance; null = resolved from SLOB. */
    planCode?: number | null;
    formulaId: string;
    name: string;
    icon: string;
    description: string;
    category: InsuranceCategory;
    durationMin?: number | null;
    durationMax?: number | null;
    minAge: number;
    maxAge: number;
    maxDurationAge: number;
    childMinAge?: number;
    childMaxAge?: number;
    childMaxDurationAge?: number;
    familyAllowed: boolean;
    visible?: boolean;
    allowBeneficiaries: boolean;
    allowDependents: boolean;
    requiresOccupation: boolean;
    benefits: string[];
    bankId?: number | null;
    currencyIds: number[];
    currencyMinPremiums?: CurrencyMinPremiumDto[];
    ageAmountLimits?: AgeAmountLimitDto[];
    questionGroupIds: number[];
    questionGroupLinks?: CreateQuestionGroupLinkDto[];
    coverages: CreateInsuranceTypeCoverageDto[];
    requirementBands: CreateRequirementBandDto[];
    seriousIllnessPlans: CreateEnfermedadesGravesPlanDto[];
    catastrophicMedicalAgePricings: CreateCatastrophicMedicalAgePricingDto[];
    coveragePlans: CreateCoveragePlanDto[];
}

export interface CreateCoveragePlanDto {
    name: string;
    items: CreateCoveragePlanItemDto[];
}

export interface CreateCoveragePlanItemDto {
    coverageName: string;
    coverageId: number;
    sumInsured: number;
    premium: number;
}

export interface CreateEnfermedadesGravesPlanDto {
    name: string;
    diagnosisPayout: number;
    survivalPayout: number;
    survivalPeriodMonths: number;
    premiums: CreateEnfermedadesGravesPremiumDto[];
    coverageIds: number[];
}

export interface CreateEnfermedadesGravesPremiumDto {
    minAge: number;
    maxAge: number;
    amount: number;
}

export interface CreateCatastrophicMedicalAgePricingDto {
    personInsuranceCurrencyId: number;
    minAge: number;
    maxAge: number;
    gender: string;
    isForChild: boolean;
    premiumAmount: number;
}

export interface CreateInsuranceTypeCoverageDto {
    coverageId: number;
    isRequired: boolean;
    insurePrimaryFraction: number;
    maxAge: number;
    maxDurationAge: number;
    isCalculated: boolean;
    tariffType?: string;
    currencies: CreateCoverageCurrencyDto[];
    categories: CreateInsuranceTypeCoverageCategoryDto[];
}

export interface CreateCoverageCurrencyDto {
    currencyId: number;
    minAmount: number;
    maxAmount: number;
    isFixedLimit?: boolean;
    /** Minimum net premium (before tax) for this coverage in this currency; null = none. */
    minPremium?: number | null;
}

export interface CreateInsuranceTypeCoverageCategoryDto {
    category: string;
    rates: CreateInsuranceTypeCoverageRateDto[];
}

export interface CreateInsuranceTypeCoverageRateDto {
    ageFrom: number;
    ageTo: number;
    rate: number;
}

export interface UpdateInsuranceTypeDto {
    lob: number;
    slob: number;
    /** Core plan code (SlobLifePlan.PlanCode) used at issuance; null = resolved from SLOB. */
    planCode?: number | null;
    formulaId: string;
    name: string;
    icon: string;
    description: string;
    category: InsuranceCategory;
    durationMin?: number | null;
    durationMax?: number | null;
    minAge: number;
    maxAge: number;
    maxDurationAge: number;
    childMinAge?: number;
    childMaxAge?: number;
    childMaxDurationAge?: number;
    familyAllowed: boolean;
    visible?: boolean;
    allowBeneficiaries: boolean;
    allowDependents: boolean;
    requiresOccupation: boolean;
    benefits: string[];
    bankId?: number | null;
    currencyIds: number[];
    currencyMinPremiums?: CurrencyMinPremiumDto[];
    ageAmountLimits?: AgeAmountLimitDto[];
    questionGroupIds: number[];
    questionGroupLinks?: CreateQuestionGroupLinkDto[];
    coverages: CreateInsuranceTypeCoverageDto[];
    requirementBands: CreateRequirementBandDto[];
    seriousIllnessPlans: CreateEnfermedadesGravesPlanDto[];
    catastrophicMedicalAgePricings: CreateCatastrophicMedicalAgePricingDto[];
    coveragePlans: CreateCoveragePlanDto[];
}

// Currency Entry DTOs
export interface CurrencyEntryDto {
    id: number;
    currency: string;
    min: number;
    max: number;
}

export interface CreateCurrencyEntryDto {
    currency: string;
    min: number;
    max: number;
}

// Question Group List DTOs
export interface QuestionGroupListDto {
    id: number;
    title: string;
    description: string;
}

export enum QuestionType {
    Text = 0,
    Bool = 1,
    File = 2,
    Conditional = 3
}

export interface CreateQuestionDto {
    title: string;
    description: string;
    type: QuestionType;
    isRequired: boolean;
    cancelOnValue?: string;
}

export interface CreateQuestionGroupEntryDto {
    title: string;
    description: string;
    questions: CreateQuestionDto[];
}

export interface UpdateQuestionDto {
    id?: number;
    title: string;
    description: string;
    type: QuestionType;
    isRequired: boolean;
    cancelOnValue?: string;
}

export interface UpdateQuestionGroupDto {
    id: number;
    title: string;
    description: string;
    questions: UpdateQuestionDto[];
}

export interface QuestionDetailDto {
    id: number;
    title: string;
    description: string;
    type: string;
    isRequired: boolean;
    cancelOnValue?: string;
    sortOrder?: number;
}

export interface QuestionGroupDetailDto {
    id: number;
    title: string;
    description: string;
    questions: QuestionDetailDto[];
}

// Coverage List DTOs
export interface CoverageListDto {
    id: number;
    coverageCode: string;
    name: string;
    description: string;
}

export interface CreateCoverageEntryDto {
    coverageCode: string;
    name: string;
    description: string;
}

export interface UpdateCoverageEntryDto {
    coverageCode: string;
    name: string;
    description: string;
}


// Requirement Band DTOs
export interface InsuranceRequirementBandDto {
    id: number;
    limits: InsuranceRequirementLimitDto[];
    ageRanges: InsuranceRequirementAgeRangeDto[];
    requirements: InsuranceRequirementDto[];
}

export interface InsuranceRequirementLimitDto {
    id: number;
    currency: string;
    amountFrom: number;
    amountTo?: number;
}

export interface InsuranceRequirementDto {
    id: number;
    name: string;
}

export interface InsuranceRequirementAgeRangeDto {
    ageFrom: number;
    ageTo: number;
}

export interface CreateRequirementBandDto {
    ageRanges: CreateRequirementAgeRangeDto[];
    limits: CreateRequirementLimitDto[];
    requirements: CreateRequirementDto[];
}

export interface CreateRequirementAgeRangeDto {
    ageFrom: number;
    ageTo: number;
}

export interface CreateRequirementLimitDto {
    currency: string;
    amountFrom: number;
    amountTo?: number;
}

export interface CreateRequirementDto {
    name: string;
}
